/*
 * 컬러 스타일 가이드
 * 
 * 이 파일은 애플리케이션 전체에서 사용되는 컬러를 관리합니다.
 * 일관된 디자인을 위해 스타일 적용 시 이 파일의 컬러를 사용해주세요.
 * 
 * @example
 * import { aigleColor } from '@styles/colorStyleGuide';
 * 
 * const StyledButton = styled.button`
 *   background-color: ${aigleColor.primary.main};
 *   color: ${aigleColor.text.white};
 * `;
 */

/*
 * 브랜드 주요 컬러
 */
export const brandColors = {
  /* 주요 브랜드 컬러  */
  primary: '#3880EF',
  primaryDark: '#147AD6',
  /* 보조 브랜드 컬러  */
  secondary: '#D6E6FD',
  secondaryDark: '#C6DDFF',
  /* 포인트 컬러  */
  point: '#F01C276',
  pointDark: '#F00A463',
} as const;

/*
 * 시맨틱 컬러 (상태 표시용)
 */
export const semanticColors = {
  /* 성공 상태 */
  success: '#ACDE5F',
  /* 경고 상태 */
  warning: '#FFB13C',
  /* 오류 상태 */
  error: '#F03551',
  /* 정보 상태 */
  info: '#86DBFF',
} as const;


/*
 * 그레이 스케일 (Grey Scale)
 */
export const greyScale = {
  white: '#FFFFFF',
  black: '#1e2225',
  lightest: '#f5f7fa',
  lighter: '#e8ecf2',
  light: '#dadfe7',
  dark: '#adbdcc',
  darker: '#8595a6',
  darkest: '#6e8199',
} as const;

/*
 * 투명도 적용 컬러
 */
export const alphaColors = {
  primary: {
    alpha10: 'rgba(39, 152, 255, 0.1)',
    alpha20: 'rgba(39, 152, 255, 0.2)',
    alpha30: 'rgba(39, 152, 255, 0.3)',
    alpha50: 'rgba(39, 152, 255, 0.5)',
  },
  secondary: {
    alpha10: 'rgba(214, 230, 253, 0.15)',
    alpha20: 'rgba(214, 230, 253, 0.25)',
    alpha30: 'rgba(214, 230, 253, 0.35)',
    alpha50: 'rgba(214, 230, 253, 0.55)',
  },
  black: {
    alpha5: 'rgba(0, 0, 0, 0.05)',
    alpha10: 'rgba(0, 0, 0, 0.1)',
    alpha20: 'rgba(0, 0, 0, 0.2)',
    alpha30: 'rgba(0, 0, 0, 0.3)',
  },
} as const;

/*
 * 그라디언트 컬러
 */
export const gradientColors = {
  /* 주요 브랜드 그라디언트 */
  primary: 'linear-gradient(135deg, #3880EF 0%, #00BFA5 100%)',
  /* 보조 브랜드 그라디언트 */
  secondary: 'linear-gradient(135deg, #86DBFF 0%, #4A9AFF 100%)',
  /* 성공 그라디언트 */
  success: 'linear-gradient(135deg, #ACDE5F 0%, #6FCF97 100%)',
  /* 경고 그라디언트 */
  warning: 'linear-gradient(135deg, #FFB13C 0%, #F8E16C 100%)',
  /* 오류 그라디언트 */
  error: 'linear-gradient(135deg, #F03551 0%, #F28B8B 100%)',
  /* 다크 그라디언트 */
  dark: 'linear-gradient(135deg, #1A1A1A 0%, #333333 100%)',
  /* 라이트 그라디언트 */
  light: 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)',
} as const;

/*
 * 차트 컬러 팔레트
 */
export const chartColors = {
  /* 기본 차트 컬러 세트 */
  default: [
    '#3880EF', // Primary
    '#86DBFF', // Secondary
    '#ACDE5F', // Success
    '#FFB13C', // Warning
    '#F03551', // Error
    '#9B51E0', // Purple
    '#F2994A', // Orange
    '#56CCF2', // Light Blue
    '#BB6BD9', // Light Purple
    '#2D9CDB', // Blue
  ],
  /* 파스텔 차트 컬러 세트 */
  pastel: [
    '#B2DFDB',
    '#B3D9FF',
    '#C8E6C9',
    '#FFF9C4',
    '#FFCCBC',
    '#E1BEE7',
    '#FFE0B2',
    '#B2EBF2',
    '#F8BBD0',
    '#C5CAE9',
  ],
} as const;

/*
 * 특수 목적 컬러
 */
export const specialColors = {
  /* 그림자 */
  shadow: 'rgba(0, 0, 0, 0.15)',
  shadowLight: 'rgba(0, 0, 0, 0.08)',
  shadowDark: 'rgba(0, 0, 0, 0.25)',
  
  /* 포커스 링 */
  focusRing: 'rgba(47, 128, 237, 0.5)',
  
  /* 스켈레톤 로딩 */
  skeleton: '#E0E0E0',
  skeletonHighlight: '#F5F5F5',
  
  /* 디바이더 */
  divider: '#E5E5E5',
  
  /* 툴팁 배경 */
  tooltip: 'rgba(51, 51, 51, 0.95)',
} as const;

/*
 * 텍스트 컬러
 */
export const textColors = {
  primary: greyScale.black,
  secondary: greyScale.darker,
  caption: greyScale.dark,
  disabled: greyScale.light,
  white: greyScale.white,
} as const;

/*
 * 배경 컬러
 */
export const backgroundColors = {
  default: greyScale.white,
  paper: greyScale.lightest,
  hover: greyScale.lightest,
  disabled: greyScale.lighter,
} as const;

/*
 * 테두리 컬러
 */
export const borderColors = {
  default: greyScale.dark,
  focus: brandColors.primary,
  disabled: greyScale.lighter,
} as const;

/*
 * 통합 컬러 가이드 객체
 */
export const aigleColor = {
  brand: brandColors,
  semantic: semanticColors,
  text: textColors,
  background: backgroundColors,
  border: borderColors,
  grey: greyScale,
  alpha: alphaColors,
  gradient: gradientColors,
  chart: chartColors,
  special: specialColors,
  
  // 편의를 위한 직접 접근 속성
  primary: brandColors.primary,
  secondary: brandColors.secondary,
  success: semanticColors.success,
  warning: semanticColors.warning,
  error: semanticColors.error,
  info: semanticColors.info,
} as const;

/*
 * TypeScript 타입 정의
 */
export type BrandColors = typeof brandColors;
export type SemanticColors = typeof semanticColors;
export type GreyScale = typeof greyScale;
export type TextColors = typeof textColors;
export type BackgroundColors = typeof backgroundColors;
export type BorderColors = typeof borderColors;
export type AlphaColors = typeof alphaColors;
export type GradientColors = typeof gradientColors;
export type ChartColors = typeof chartColors;
export type SpecialColors = typeof specialColors;
export type AigleColor = typeof aigleColor;

// Default export
export default aigleColor;

